import { round2, round4, roundHalfUpCents } from '../money';
import { t } from '../strings';
import { calendarDayInZone, isoDay, wallClockInZone } from '../zonedTime';
import type { LaborModifierComposition } from './settings';

/**
 * The labour rate model (piece 22; decisions 0083, 0115, 0124, 0174, 0246,
 * 0248, 0381). Six inputs, and they live on the line and its workers: the
 * required skill level, the performer, the base rate, `priorityModifier`,
 * `afterHoursModifier` and the overtime fact. Everything here is pure — the
 * service resolves the rows and this module says what they add up to — so
 * the invoice can be explained rather than merely reproduced.
 *
 * 🔨 After-hours and overtime are two different facts and never one field:
 * after-hours is when the work happened and modifies `billingRate`; overtime
 * is the employee's week and modifies `costRate`. They are computed apart.
 */
export class LabourError extends Error {}

// ---- Composition (decision 0083) ----

/** All three modes read the same stored factors: 1.5 means ×1.5 in every installation (decision 0080). */
export function composeModifiers(mode: LaborModifierComposition, factors: readonly number[]): number {
  const applied = factors.filter((f) => Number.isFinite(f) && f > 0);
  if (applied.length === 0) return 1;
  switch (mode) {
    case 'Multiply':
      return round4(applied.reduce((product, f) => product * f, 1));
    case 'Additive':
      return round4(1 + applied.reduce((sum, f) => sum + (f - 1), 0));
    case 'HighestSingle':
    default:
      return round4(Math.max(1, ...applied));
  }
}

// ---- The base rate (decisions 0246, 0248, 0381) ----

export type BaseRateSource = 'LabourItem' | 'PriceSheet' | 'AgreementOverride';

export interface BaseRateInput {
  /** The salesPrice of the labour Item the worker's SkillLevel names; null where the level names no Item. */
  labourItemSalesPrice: number | null;
  /** A PriceSheet rule's price for that Item, where the customer or the agreement carries one. */
  priceSheetRate?: number | null;
  /** `Agreement.laborRateOverride`, which wins the base rate outright. */
  agreementLaborRateOverride?: number | null;
}

/** The level's Item price, then the sheet's rule on that Item, then the override, which wins (decision 0248). */
export function resolveBaseRate(input: BaseRateInput): { rate: number; source: BaseRateSource } {
  if (input.agreementLaborRateOverride !== null && input.agreementLaborRateOverride !== undefined) return { rate: input.agreementLaborRateOverride, source: 'AgreementOverride' };
  if (input.priceSheetRate !== null && input.priceSheetRate !== undefined) return { rate: input.priceSheetRate, source: 'PriceSheet' };
  if (input.labourItemSalesPrice === null || input.labourItemSalesPrice === undefined) throw new LabourError('the skill level names no labor Item, so the participation has no rate');
  return { rate: input.labourItemSalesPrice, source: 'LabourItem' };
}

export interface WorkerCandidate {
  /** The SkillLevel the worker's position holds; null on a position HR has not levelled. */
  positionSkillLevelId: string | null;
  positionSkillLevelName?: string | null;
  /** The labour Item that level names; null until Settings names one (decision 0381). */
  levelLabourItemId: string | null;
  /** True where the employment has a CompensationRecord in force on the work date. */
  hasCompensation: boolean;
}

/**
 * Why a participation is refused, or null. 💰 A new hire bills nothing until
 * HR sets a level (decision 0246), a level prices nothing until it names an
 * Item (decision 0381), and a cost of zero is the silent under-costing the
 * model exists to prevent, so a worker with no pay row is refused too.
 */
export function workerRefusal(candidate: WorkerCandidate): string | null {
  if (!candidate.positionSkillLevelId) return 'this position has no skill level; set one on the employment page before adding the worker';
  if (!candidate.levelLabourItemId) return `${candidate.positionSkillLevelName ?? 'the skill level'} names no labor Item; choose one under Settings › Service › Labor and Skill Levels`;
  if (!candidate.hasCompensation) return 'this employment has no pay rate in force on the work date; the Compensation tile on the Person page records one';
  return null;
}

/** Standard full-time hours a year, the divisor a yearly pay rate becomes an hourly cost by. */
export const HOURS_PER_YEAR = 2080;

export interface CompensationLike {
  payRate: number;
  payRateUnit: 'PerHour' | 'PerYear';
}

/** The hourly cost the worker row copies from the pay row in force on the work date. */
export function costRateFrom(compensation: CompensationLike | null): number | null {
  if (!compensation) return null;
  return compensation.payRateUnit === 'PerHour' ? compensation.payRate : round2(compensation.payRate / HOURS_PER_YEAR);
}

// ---- After-hours (decision 0115) ----

export interface BusinessHoursLike {
  /** 0 is Sunday through 6 is Saturday, the JavaScript convention. */
  dayOfWeek: number;
  /** HH:mm, local to the Organization. */
  opensAt: string;
  closesAt: string;
  effectiveFrom: Date;
  effectiveTo: Date | null;
}

export interface HolidayLike {
  date: Date;
  isObserved: boolean;
}

export type AfterHoursReason = 'WithinHours' | 'OutsideBusinessHours' | 'Weekend' | 'Holiday' | 'NoHoursConfigured';

export interface AfterHoursProposal {
  afterHours: boolean;
  reason: AfterHoursReason;
  /** The factor the line persists: the premium where after-hours, 1 otherwise. */
  modifier: number;
}

/** The day a holiday is kept: a weekend date moves to the adjacent weekday when the row says observed (decision 0115). */
export function observedHolidayDate(row: HolidayLike): string {
  const date = new Date(`${isoDay(row.date)}T00:00:00.000Z`);
  if (!row.isObserved) return isoDay(date);
  const day = date.getUTCDay();
  if (day === 6) date.setUTCDate(date.getUTCDate() - 1);
  if (day === 0) date.setUTCDate(date.getUTCDate() + 1);
  return isoDay(date);
}

/**
 * The proposal from the work's own timestamp: a holiday row, then a day with
 * no hours, then the opening and closing times. A person overrides it with a
 * reason and the resolved value is persisted. 💰 With no `BusinessHours` row in
 * force at all the proposal is no premium, because nothing has said when the
 * business is open; once any row is in force, a day with none is after-hours.
 */
export function proposeAfterHours(input: { at: Date; zone: string; businessHours: readonly BusinessHoursLike[]; holidays: readonly HolidayLike[]; factor: number }): AfterHoursProposal {
  const wall = wallClockInZone(input.at, input.zone);
  const date = calendarDayInZone(input.at, input.zone);
  const time = wall.slice(11, 16);
  const premium = (reason: AfterHoursReason): AfterHoursProposal => ({ afterHours: true, reason, modifier: input.factor });
  if (input.holidays.some((row) => observedHolidayDate(row) === date)) return premium('Holiday');
  const inForce = input.businessHours.filter((row) => isoDay(row.effectiveFrom) <= date && (row.effectiveTo === null || isoDay(row.effectiveTo) >= date));
  if (inForce.length === 0) return { afterHours: false, reason: 'NoHoursConfigured', modifier: 1 };
  const weekday = new Date(`${date}T00:00:00.000Z`).getUTCDay();
  const today = inForce.filter((row) => row.dayOfWeek === weekday);
  if (today.length === 0) return premium(weekday === 0 || weekday === 6 ? 'Weekend' : 'OutsideBusinessHours');
  const open = today.some((row) => row.opensAt <= time && time < row.closesAt);
  return open ? { afterHours: false, reason: 'WithinHours', modifier: 1 } : premium('OutsideBusinessHours');
}

// ---- Hours, billability and the composed rate ----

export interface WorkerLike {
  employmentId?: string;
  hours: number;
  billingRate: number;
  costRate?: number | null;
  isBillable: boolean;
  /** The worker's own level, from the position; the participation's required level. */
  skillLevel?: number | null;
  requiredLevel?: number | null;
}

export interface LineLike<W extends WorkerLike = WorkerLike> {
  priorityModifier: number;
  afterHoursModifier: number;
  compositionMode: LaborModifierComposition;
  isBillable: boolean;
  workers: readonly W[];
}

/** 🔨 The line's hours are the sum of its worker rows; no column holds them. */
export function lineHours(line: Pick<LineLike, 'workers'>): number {
  return round2(line.workers.reduce((sum, w) => sum + w.hours, 0));
}

/** An unbillable line makes every worker row on it unbillable; a billable line honours each row's own flag. */
export function workerBillable(line: Pick<LineLike, 'isBillable'>, worker: Pick<WorkerLike, 'isBillable'>): boolean {
  return line.isBillable && worker.isBillable;
}

/** The factor the line's stored components compose to, under the mode stored beside them. */
export function composedFactor(line: Pick<LineLike, 'priorityModifier' | 'afterHoursModifier' | 'compositionMode'>): number {
  return composeModifiers(line.compositionMode, [line.priorityModifier, line.afterHoursModifier]);
}

/**
 * 🔒 What one participation bills on its line, in cents, the way the invoice
 * from the job writes it (T1900): its hours at the worker's own rate, and the
 * line's premium on the same hours as a figure of its own (piece 22: the
 * premiums print as separate invoice lines). Each figure rounds once, half up,
 * as an invoice line's amount does, so the Work Order page and the invoice
 * add up to the same cents. Nothing where the participation is unbillable.
 */
export interface WorkerCharge {
  /** The line's modifiers composed under its mode (decision 0083). */
  factor: number;
  /** The worker's own rate, per hour, in cents. */
  rateCents: number;
  /** The premium per hour in cents: the rate times the factor less one. */
  premiumRateCents: number;
  baseCents: number;
  premiumCents: number;
  totalCents: number;
}

export function workerCharge(line: Pick<LineLike, 'priorityModifier' | 'afterHoursModifier' | 'compositionMode' | 'isBillable'>, worker: Pick<WorkerLike, 'hours' | 'billingRate' | 'isBillable'>): WorkerCharge {
  return workerChargeAtRate(line, { hours: worker.hours, billingRateCents: roundHalfUpCents(worker.billingRate * 100), isBillable: worker.isBillable });
}

/**
 * The same charge from a rate already in whole cents (T2192 item 12): the Work Order document carries each worker's
 * rate as cents, and the invoice from the job prices their hours from it without a trip through dollars.
 */
export function workerChargeAtRate(line: Pick<LineLike, 'priorityModifier' | 'afterHoursModifier' | 'compositionMode' | 'isBillable'>, worker: { hours: number; billingRateCents: number; isBillable: boolean }): WorkerCharge {
  const factor = composedFactor(line);
  const rateCents = worker.billingRateCents;
  const premiumRateCents = factor === 1 ? 0 : roundHalfUpCents((rateCents * (factor - 1)));
  if (!workerBillable(line, worker)) return { factor, rateCents, premiumRateCents, baseCents: 0, premiumCents: 0, totalCents: 0 };
  const baseCents = roundHalfUpCents(worker.hours * rateCents);
  const premiumCents = roundHalfUpCents(worker.hours * premiumRateCents);
  return { factor, rateCents, premiumRateCents, baseCents, premiumCents, totalCents: baseCents + premiumCents };
}

/**
 * The premium line's name on the invoice (T1900): the modifier or modifiers
 * that set the factor, and the factor they compose to, or null where the factor
 * is one and no premium line is written. Under HighestSingle only the larger of
 * the two sets the factor, so only it is named; a tie names both (T2004).
 */
export function premiumLabel(line: Pick<LineLike, 'priorityModifier' | 'afterHoursModifier' | 'compositionMode'>): string | null {
  const factor = composedFactor(line);
  if (factor === 1) return null;
  const priority = Number.isFinite(line.priorityModifier) && line.priorityModifier > 0 && line.priorityModifier !== 1;
  const afterHours = Number.isFinite(line.afterHoursModifier) && line.afterHoursModifier > 0 && line.afterHoursModifier !== 1;
  // 🔨 Under HighestSingle the smaller modifier changed nothing, so the label does not name it.
  const single = line.compositionMode === 'HighestSingle' && priority && afterHours && line.priorityModifier !== line.afterHoursModifier;
  const named = single ? (line.priorityModifier > line.afterHoursModifier ? 'priority' : 'afterHours') : priority && afterHours ? 'both' : afterHours ? 'afterHours' : 'priority';
  const name = named === 'both' ? t('jobBilling.premium.both') : named === 'afterHours' ? t('jobBilling.premium.afterHours') : t('jobBilling.premium.priority');
  return `${name} ×${fmt(factor)}`;
}

/** What one participation bills: the charge above, in dollars; nothing where unbillable. */
export function workerBilledAmount(line: Pick<LineLike, 'priorityModifier' | 'afterHoursModifier' | 'compositionMode' | 'isBillable'>, worker: WorkerLike): number {
  return workerCharge(line, worker).totalCents / 100;
}

/** The line's billed labour in whole cents: the sum over its participations' charges, which no single per-line rate reproduces. */
export function lineBilledCents(line: LineLike): number {
  return line.workers.reduce((sum, w) => sum + workerCharge(line, w).totalCents, 0);
}

/** The same figure in dollars, for the working a person reads. */
export function lineBilledAmount(line: LineLike): number {
  return lineBilledCents(line) / 100;
}

/**
 * What one participation costs, in cents (T2192 item 4): its hours at its own cost rate, rounded once, half up,
 * billable or not. The Work Order page, the Labour report and the Project Performance report all read this
 * figure, so none of them rounds a cost of its own.
 */
export function workerCostCents(worker: Pick<WorkerLike, 'hours' | 'costRate'>): number {
  return roundHalfUpCents(worker.hours * (worker.costRate ?? 0) * 100);
}

/** The line's labour cost: each worker's cost in whole cents, summed, billable or not. */
export function lineCostAmount(line: Pick<LineLike, 'workers'>): number {
  return line.workers.reduce((sum, w) => sum + workerCostCents(w), 0) / 100;
}

/**
 * The working behind a line's billed amount, one line per participation and
 * the composition named, for the super admin's control (decision 0375). It
 * reads the stored components and never re-derives the rate.
 */
export function rateDerivation(line: LineLike, names: (worker: WorkerLike, index: number) => string = (_w, i) => `worker ${i + 1}`): string[] {
  const factor = composedFactor(line);
  const lines = line.workers.map((w, i) => {
    const name = names(w, i);
    if (!workerBillable(line, w)) return `${name}: ${fmt(w.hours)} h unbilled${line.isBillable ? '' : ' (line unbillable)'}`;
    return `${name}: ${fmt(w.hours)} h × ${fmt(w.billingRate)} × ${fmt(factor)} = ${fmt(workerBilledAmount(line, w))}`;
  });
  lines.push(`modifiers: priority ${fmt(line.priorityModifier)} · after-hours ${fmt(line.afterHoursModifier)} · ${line.compositionMode} → ${fmt(factor)}`);
  lines.push(`line: ${fmt(lineHours(line))} h · ${fmt(lineBilledAmount(line))}`);
  return lines;
}

// ---- Levels, situations and the follow-up (decisions 0023, 0246) ----

/** Higher where the person sent outranks the participation's level, Lower where they fall short; the report reads both. */
export function levelMismatch(worker: Pick<WorkerLike, 'skillLevel' | 'requiredLevel'>): 'Higher' | 'Lower' | null {
  if (worker.skillLevel === null || worker.skillLevel === undefined || worker.requiredLevel === null || worker.requiredLevel === undefined) return null;
  if (worker.skillLevel > worker.requiredLevel) return 'Higher';
  if (worker.skillLevel < worker.requiredLevel) return 'Lower';
  return null;
}

// ---- Sub-skills and the dispatch warning (decision 0464) ----

/**
 * A sub-skill as the line names it and the person holds it: the row's own id,
 * what it is called, and the skill it sits beneath. 🔨 `skillId` is what makes
 * a skill held whole cover it (decision 0614); a caller that cannot say which
 * skill a sub-skill belongs to leaves it out and the whole-skill rows then
 * cover nothing.
 */
export interface SubSkillLike {
  id: string;
  name: string;
  skillId?: string;
}

/**
 * The sub-skills a line needs that a worker does not hold (decision 0464), less
 * the ones covered by a skill they hold whole (decision 0614). A skill held
 * whole covers every sub-skill beneath it, so a sub-skill configured after the
 * holding was recorded is covered the moment it exists.
 *
 * 🔨 The answer is a warning and never a refusal: the caller adds the worker
 * and dispatches the job whatever this returns. `Skill.certificationRequired`
 * stays the only refusal in this area, because a dispatcher who meets two
 * stops reads neither.
 */
export function missingSubSkills(
  required: readonly SubSkillLike[],
  heldSubSkillIds: readonly string[],
  heldSkillIds: readonly string[] = [],
): SubSkillLike[] {
  const held = new Set(heldSubSkillIds);
  const whole = new Set(heldSkillIds);
  return required.filter((row) => !held.has(row.id) && !(row.skillId !== undefined && whole.has(row.skillId)));
}

/**
 * What the warning says, or null where there is nothing to warn about. It
 * names the person and every sub-skill they do not hold, because a banner
 * naming the problem without naming who it is about sends the dispatcher
 * looking through the line for it.
 */
export function subSkillWarningText(personName: string, missing: readonly SubSkillLike[], phrase: string): string | null {
  if (missing.length === 0) return null;
  return `${personName} ${phrase} ${missing.map((row) => row.name).join(', ')}`;
}

export const REQUIRES_HIGHER_SKILL_LEVEL = 'RequiresHigherSkillLevel';

/**
 * The reason codes a stopped line names (decision 0023): specific, never free
 * text. 🔒 The five piece 29 names and nothing else saves (decision 0731); a
 * row holding a retired code keeps it and reads it as it was written.
 */
export const NOT_COMPLETE_REASONS = ['MissingPart', 'NoSiteAccess', 'RanOutOfDaylight', 'WrongEquipment', REQUIRES_HIGHER_SKILL_LEVEL] as const;
export type NotCompleteReason = (typeof NOT_COMPLETE_REASONS)[number];

/**
 * What each reason code reads as on a screen. 🔨 A picker offers the sentence
 * and stores the member; it never draws the member itself, which is a
 * stored-value spelling and not language (found in review, 2026-09-15).
 */
export const NOT_COMPLETE_REASON_LABELS: Readonly<Record<NotCompleteReason, string>> = {
  MissingPart: t('workOrder.line.reasonMissingPart'),
  NoSiteAccess: t('workOrder.line.reasonNoSiteAccess'),
  RanOutOfDaylight: t('workOrder.line.reasonRanOutOfDaylight'),
  WrongEquipment: t('workOrder.line.reasonWrongEquipment'),
  RequiresHigherSkillLevel: t('workOrder.line.reasonRequiresHigherSkillLevel'),
};

export type BillingSituation = 'A' | 'B' | 'C';

/** The three situations of decision 0246, as what happened rather than as a letter. */
export const BILLING_SITUATION_LABELS: Readonly<Record<BillingSituation, string>> = {
  A: t('workOrder.line.situationA'),
  B: t('workOrder.line.situationB'),
  C: t('workOrder.line.situationC'),
};

export const LINE_COMPLETION_VALUES = ['Pending', 'Complete', 'NotComplete', 'NotNeeded'] as const;
export type LineCompletion = (typeof LINE_COMPLETION_VALUES)[number];

export interface CompletionPatch {
  completionStatus: LineCompletion;
  notCompleteReason?: string | null;
  missingSkillId?: string | null;
}

/** 🔨 NotComplete requires a reason, and a stop for a higher level names the skill the scope never named. */
export function completionProblem(patch: CompletionPatch): string | null {
  if (patch.completionStatus !== 'NotComplete') return null;
  if (!patch.notCompleteReason) return 'a line that did not complete says why: choose a reason code';
  if (!(NOT_COMPLETE_REASONS as readonly string[]).includes(patch.notCompleteReason)) return `${patch.notCompleteReason} is not a reason code; the codes are ${NOT_COMPLETE_REASONS.join(', ')}`;
  if (patch.notCompleteReason === REQUIRES_HIGHER_SKILL_LEVEL && !patch.missingSkillId) return 'a stop for a higher skill level names the skill that was missing';
  return null;
}


export interface SituationLine {
  completionStatus: LineCompletion;
  notCompleteReason?: string | null;
  /** The level number the line demands. */
  requiredLevel: number | null;
  workers: readonly { skillLevel: number | null; hours: number }[];
}

/**
 * Which of the three situations a Level 1 meeting Level 3 work produced
 * (decision 0246): A diagnosed and stopped, B started and handed over, C did
 * the whole job anyway. Every situation bills each worker at that worker's
 * own level; C is flagged as a Site Requirements violation and a safety question.
 */
export function billingSituation(line: SituationLine): { situation: BillingSituation | null; flags: string[] } {
  const required = line.requiredLevel;
  const levelled = line.workers.filter((w) => w.skillLevel !== null && w.hours > 0);
  if (line.completionStatus === 'NotComplete' && line.notCompleteReason === REQUIRES_HIGHER_SKILL_LEVEL) return { situation: 'A', flags: [] };
  if (line.completionStatus !== 'Complete' || required === null || levelled.length === 0) return { situation: null, flags: [] };
  const below = levelled.filter((w) => (w.skillLevel ?? 0) < required);
  const atOrAbove = levelled.filter((w) => (w.skillLevel ?? 0) >= required);
  if (below.length > 0 && atOrAbove.length > 0) return { situation: 'B', flags: [] };
  if (below.length > 0 && atOrAbove.length === 0) return { situation: 'C', flags: ['SiteRequirementsViolation'] };
  return { situation: null, flags: [] };
}

export interface LevelRow {
  id: string;
  level: number;
  isActive: boolean;
}

/** The next level up for a follow-up line escalated after a stop, or the same level for any other reason. */
export function escalatedLevelId(levels: readonly LevelRow[], currentLevelId: string, reason: string | null | undefined): string {
  if (reason !== REQUIRES_HIGHER_SKILL_LEVEL) return currentLevelId;
  const current = levels.find((l) => l.id === currentLevelId);
  if (!current) return currentLevelId;
  const above = levels.filter((l) => l.isActive && l.level > current.level).sort((a, b) => a.level - b.level)[0];
  return above?.id ?? currentLevelId;
}

/** Create Follow-Up carries the NotComplete lines and nothing else (decision 0023). */
export function followUpLines<T extends { completionStatus: LineCompletion }>(lines: readonly T[]): T[] {
  return lines.filter((l) => l.completionStatus === 'NotComplete');
}

/** The trip's billable answer: the code's flag unless overridden, and an override carries its reason. */
export function followUpBillable(code: { isBillable: boolean } | null, override: boolean | null | undefined, overrideReason: string | null | undefined): { billable: boolean | null; problem: string | null } {
  if (override !== null && override !== undefined) {
    if (!overrideReason?.trim()) return { billable: null, problem: 'overriding the reason code’s billable flag records why' };
    return { billable: override, problem: null };
  }
  return { billable: code ? code.isBillable : null, problem: null };
}

/** The highest proficiency the Deal's skill requirements demand; a requested person raises it upward only. */
export function requiredLevelFor(levels: readonly LevelRow[], requirementLevelIds: readonly string[], requestedLevelId: string | null = null): string | null {
  const byId = new Map(levels.map((l) => [l.id, l]));
  const candidates = [...requirementLevelIds, ...(requestedLevelId ? [requestedLevelId] : [])].map((id) => byId.get(id)).filter((l): l is LevelRow => Boolean(l));
  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => b.level - a.level)[0]!.id;
}

// ---- Overtime (decision 0124) ----

/** Time and a half, the Fair Labor Standards Act default; HR refines it by classification. */
export const OVERTIME_COST_MULTIPLIER = 1.5;
export const OVERTIME_THRESHOLD_HOURS = 40;

export interface TimeEntryLike {
  /** The work date as an ISO date, YYYY-MM-DD. */
  workDate: string;
  hours: number;
}

export interface WorkweekLike {
  /** 0 is Sunday through 6 is Saturday. */
  startsOnDayOfWeek: number;
}

/** The ISO date the workweek containing a date starts on. */
export function workweekStart(workDate: string, week: WorkweekLike): string {
  const date = new Date(`${workDate}T00:00:00.000Z`);
  const back = (date.getUTCDay() - week.startsOnDayOfWeek + 7) % 7;
  date.setUTCDate(date.getUTCDate() - back);
  return isoDay(date);
}

export interface WeekOvertime {
  weekStart: string;
  regularHours: number;
  overtimeHours: number;
}

/**
 * Overtime is a weekly fact: hours past the threshold in the fixed, recurring
 * workweek, never a flag on a line. Nobody works overtime on a line.
 */
export function overtimeByWeek(entries: readonly TimeEntryLike[], week: WorkweekLike, threshold = OVERTIME_THRESHOLD_HOURS): WeekOvertime[] {
  const weeks = new Map<string, number>();
  for (const entry of entries) {
    const start = workweekStart(entry.workDate, week);
    weeks.set(start, (weeks.get(start) ?? 0) + entry.hours);
  }
  return [...weeks.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([weekStart, hours]) => ({
    weekStart,
    regularHours: round2(Math.min(hours, threshold)),
    overtimeHours: round2(Math.max(0, hours - threshold)),
  }));
}

/** The cost side of an overtime hour; the billing side never reads this. */
export function costWithOvertime(costRate: number, overtime: boolean): number {
  return overtime ? round2(costRate * OVERTIME_COST_MULTIPLIER) : costRate;
}

// ---- Priority from notice (piece 29's derivation, the fields declared here) ----

export interface TierLike {
  id: string;
  maxNoticeHours: number;
}

/**
 * Priority derives from `scheduledStart` minus `createdAt` against the dated
 * tier rows: the tier with the smallest bound at or above the notice. 💰 An
 * unbooked job has no due start, so it resolves the widest tier and re-derives
 * when booked; the tier freezes at dispatch.
 */
export function priorityTierFor(createdAt: Date, scheduledStart: Date | null, tiers: readonly TierLike[]): { tierId: string | null; noticeHours: number | null } {
  const ordered = [...tiers].sort((a, b) => a.maxNoticeHours - b.maxNoticeHours);
  if (ordered.length === 0) return { tierId: null, noticeHours: null };
  const widest = ordered[ordered.length - 1]!;
  if (!scheduledStart) return { tierId: widest.id, noticeHours: null };
  const noticeHours = round2(Math.max(0, (scheduledStart.getTime() - createdAt.getTime()) / 3_600_000));
  const tier = ordered.find((t) => t.maxNoticeHours >= noticeHours) ?? widest;
  return { tierId: tier.id, noticeHours };
}

// ---- Who sees the cost (decision 0128 applied to the line) ----

export interface CostViewer {
  personId: string;
  isHr: boolean;
  isAdmin: boolean;
}

/** 🔨 `costRate` is visible to HR, Admin and the worker only; everyone else sees the billing side and never the cost. */
export function costRateVisible(viewer: CostViewer, worker: { personId: string }): boolean {
  return viewer.isHr || viewer.isAdmin || viewer.personId === worker.personId;
}

/** The worker row as it leaves the server: the column omitted, never blanked, for a viewer the rule refuses. */
export function serialiseWorker<T extends { costRate: unknown; personId: string }>(row: T, viewer: CostViewer): Omit<T, 'costRate'> | T {
  if (costRateVisible(viewer, row)) return row;
  const { costRate: _withheld, ...rest } = row;
  return rest;
}

// ---- helpers ----

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : String(round2(n));
}
